"use client";

import { useMemo } from "react";
import { usePersistedState } from "@/lib/finance/usePersistedState";
import { addMonths } from "@/lib/finance/format";
import { asDate, resolveRange, PAGE_RANGE_PRESETS, type RangePreset, type RangeState } from "@/lib/finance/range";
import { Segmented } from "@/components/finance/ui";

/**
 * Reusable time-range control: preset chips (1Y/3Y/…/All) plus a Custom option
 * that reveals From/To month inputs. The selection is persisted per storageKey
 * so it's remembered across navigation and reloads. The pure range math
 * (presets, resolution, injected-window → preset mapping) lives in
 * lib/finance/range.ts.
 *
 * Consumers get a resolved `[startMonth, endMonth]` window (YYYY-MM inclusive);
 * "All" resolves to [dataStart, dataEnd]. Filter your data on that window
 * rather than slicing a fixed count.
 */

export { PAGE_RANGE_PRESETS, rangeLabel, rangeStateFor } from "@/lib/finance/range";
export type { RangePreset, RangeState } from "@/lib/finance/range";

/**
 * Page-level range: one shared storage key + shared presets + a 1Y default,
 * resolved against each page's own [dataStart, dataEnd]. Every page uses this
 * so the picker is aligned and the window persists as you move between pages.
 */
export function usePageRange(dataStart: string, dataEnd: string) {
  return useRange("page", PAGE_RANGE_PRESETS, "1y", dataStart, dataEnd);
}

export function useRange(
  storageKey: string,
  presets: RangePreset[],
  defaultPreset: string,
  dataStart: string,
  dataEnd: string,
) {
  const [state, setState] = usePersistedState<RangeState>(`fin.range.${storageKey}`, {
    kind: "preset",
    preset: defaultPreset,
    from: `${addMonths(dataEnd, -12)}-01`, // custom defaults to the trailing year
    to: asDate(dataEnd, true),
  });

  const [startMonth, endMonth] = useMemo<[string, string]>(
    () => resolveRange(state, presets, defaultPreset, dataStart, dataEnd),
    [state, presets, defaultPreset, dataStart, dataEnd],
  );

  const control = (
    <RangeControl state={state} setState={setState} presets={presets} dataStart={dataStart} dataEnd={dataEnd} />
  );

  return { startMonth, endMonth, state, setState, control };
}

function RangeControl({
  state,
  setState,
  presets,
  dataStart,
  dataEnd,
}: {
  state: RangeState;
  setState: (s: RangeState) => void;
  presets: RangePreset[];
  dataStart: string;
  dataEnd: string;
}) {
  const customActive = state.kind === "custom";

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Segmented
        ariaLabel="Time range"
        options={[
          ...presets.map((p) => ({
            value: p.value,
            label: p.label,
            className: p.mobileHidden ? "hidden sm:block" : undefined,
          })),
          { value: "custom", label: "Custom" },
        ]}
        value={customActive ? "custom" : state.preset}
        onChange={(v) =>
          v === "custom"
            ? setState({ ...state, kind: "custom" })
            : setState({ ...state, kind: "preset", preset: v })
        }
      />

      {customActive && (
        <div className="inline-flex items-center gap-1.5 rounded-full border border-edge bg-surface-1 px-2.5 py-1 text-xs text-ink-2">
          <input
            type="date"
            value={state.from.length >= 10 ? state.from : `${state.from}-01`}
            min={asDate(dataStart, false)}
            max={asDate(dataEnd, true)}
            onChange={(e) => setState({ ...state, kind: "custom", from: e.target.value })}
            aria-label="From date"
            className="bg-transparent text-ink [color-scheme:light]"
          />
          <span className="text-ink-3">→</span>
          <input
            type="date"
            value={state.to.length >= 10 ? state.to : `${state.to}-31`}
            min={asDate(dataStart, false)}
            max={asDate(dataEnd, true)}
            onChange={(e) => setState({ ...state, kind: "custom", to: e.target.value })}
            aria-label="To date"
            className="bg-transparent text-ink [color-scheme:light]"
          />
        </div>
      )}
    </div>
  );
}
